import { useEffect, useState } from 'react';
import { Button, Drawer, Empty, Input, Pagination, Skeleton } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { PersonAvatar, Pill } from 'pages/support-shared/components';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import { Breakable, apiErrorText, fmtNumber, isMissingEndpoint, unwrapPage } from './common';
import { MemberTypeCard, MemberTypeUser } from './types';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;

// Mã máy chủ gửi (hp-media-server-staging/src/member-types/member-types.errors.ts):
// MEMBER_TYPE_INVALID (400) và MEMBER_TYPES_ADMIN_ONLY (409, MOD gọi danh sách từng người).
const ERRORS: Record<string, string> = {
  MEMBER_TYPE_INVALID: 'Không có nhóm người này. Tải lại trang rồi thử lại.',
  MEMBER_TYPES_ADMIN_ONLY: 'Chỉ Admin xem được danh sách từng người.',
};

// Tên vai trò theo cách gọi đã chốt (Creator viết hoa, không dùng "Idol").
const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  MOD: 'Quản lý tổng (SMOD)',
  AGENCY: 'Agency',
  CREATOR: 'Creator Online',
  STUDIO_CREATOR: 'Creator Studio',
  USER: 'User',
};

// MOD thuộc phòng "QL Vận Hành" là cấp "Quản lý vận hành" (cùng luật với thẻ nhóm ở máy chủ).
const roleLabel = (role: string, department?: string | null) =>
  role === 'MOD' && department === 'MANAGER' ? 'Quản lý vận hành' : ROLE_LABEL[role] || role;

function rolesOf(roles?: string | string[] | null): string[] {
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string') {
    return roles
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean);
  }
  return [];
}

const nameOf = (user: MemberTypeUser) =>
  user.nickName || user.name || user.username || user.email || `Tài khoản số ${user.id}`;

interface Props {
  group: MemberTypeCard | null;
  onClose: () => void;
}

/** Danh sách người của một nhóm (chỉ ADMIN): ô tìm + phân trang 20. */
const MembersDrawer = ({ group, onClose }: Props) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [items, setItems] = useState<MemberTypeUser[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const groupKey = group?.key;

  // Mở nhóm khác: về trang 1, bỏ ô tìm.
  useEffect(() => {
    setPage(1);
    setSearch('');
    setQ('');
    setItems([]);
    setTotal(null);
    setError('');
  }, [groupKey]);

  useEffect(() => {
    if (!groupKey) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const params: Record<string, string | number> = { type: groupKey, page, limit: PAGE_SIZE };
    if (q) params.q = q;
    AxiosUtils.get(API.memberTypesUsers, params)
      .then((res: any) => {
        if (cancelled) return;
        const next = unwrapPage<MemberTypeUser>(res);
        setItems(next.items);
        setTotal(next.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(
          isMissingEndpoint(err)
            ? 'Danh sách từng người đang được bật trên máy chủ. Quay lại sau ít phút.'
            : apiErrorText(err, ERRORS, 'Không tải được danh sách. Vui lòng thử lại.')
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupKey, page, q, attempt]);

  let body: React.ReactNode;
  if (loading && !items.length) {
    body = <Skeleton active avatar paragraph={{ rows: 1 }} title={{ width: '60%' }} />;
  } else if (error) {
    body = (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error}>
        <Button icon={<ReloadOutlined />} onClick={() => setAttempt((n) => n + 1)}>
          Thử lại
        </Button>
      </Empty>
    );
  } else if (!items.length) {
    body = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          q ? `Không thấy ai khớp với “${q}” trong nhóm này.` : 'Nhóm này hiện chưa có ai.'
        }
      />
    );
  } else {
    body = (
      <div style={{ opacity: loading ? 0.55 : 1 }} aria-busy={loading}>
        {items.map((user) => {
          const department =
            user.departmentLabel ||
            (user.department ? DEPARTMENT_LABEL[user.department] || user.department : '');
          const roles = rolesOf(user.roles)
            .map((role) => roleLabel(role, user.department))
            .join(', ');
          return (
            <div key={user.id} className={styles.person}>
              <PersonAvatar name={nameOf(user)} avatar={user.avatar} size={36} />
              <div className={styles.personText}>
                <span className={styles.personName}>{nameOf(user)}</span>
                {user.email && (
                  <span className={styles.personSub}>
                    <Breakable text={user.email} />
                  </span>
                )}
                {user.username && (
                  <span className={styles.personSub}>
                    <Breakable text={`@${user.username}`} />
                  </span>
                )}
              </div>
              <div className={styles.personSide}>
                {roles && <span>{roles}</span>}
                {department && <span>{department}</span>}
                {user.isAccountDisabled && (
                  <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
                    Đang khoá
                  </Pill>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Drawer
      open={!!group}
      onClose={onClose}
      width="min(560px, 100vw)"
      title={group ? `${group.label}: danh sách` : ''}
      destroyOnHidden
    >
      <Input.Search
        allowClear
        // Chữ gợi ý ngắn để không bị cắt "…" trên màn 360px; câu đầy đủ nằm ở aria-label.
        aria-label="Tìm theo tên, TikTok ID hoặc email"
        placeholder="Tên, TikTok ID hoặc email"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onSearch={(value) => {
          setPage(1);
          setQ(value.trim());
        }}
      />
      <div className={styles.drawerTotal}>
        {total === null ? '' : `${fmtNumber(total)} người${q ? ' khớp ô tìm' : ''}`}
      </div>
      {body}
      {!error && (total ?? 0) > PAGE_SIZE && (
        <div className={styles.drawerPager}>
          <Pagination
            simple
            current={page}
            pageSize={PAGE_SIZE}
            total={total ?? 0}
            showSizeChanger={false}
            onChange={setPage}
          />
        </div>
      )}
    </Drawer>
  );
};

export default MembersDrawer;
